import React, {useEffect, useState} from "react";
import {useAbortController, useAccessInfo, useAppDispatch, useAppSelector} from "../../../hooks";
import {Role} from "../../../models/Role";
import {useParams} from "react-router";
import {selectToken} from "../../../features/account/accountSlice";
import {Record} from "../../../models/Record";
import ErrorResponse from "../../../models/ErrorResponse";
import handleErrors from "../../../helpers/ErrorHandler";
import * as ErrorCodes from "../../../api/ErrorCodes";
import * as ApiHelper from "../../../api/ApiHelper";
import {Breadcrumbs} from "../../Widgets/Breadcrumbs/Breadcrumbs";
import {formatDateTime} from "../../../helpers/FormatHelper";
import AsyncIndicator from "../../Widgets/AsyncIndicator/AsyncIndicator";
import {FetchError} from "../../Widgets/FetchError/FetchError";
import NotFound from "../../Widgets/NotFound/NotFound";
import {Conclusion} from "../../Widgets/Conclusion/Conclusion";
import {SpiroData} from "../../../models/SpiroData";
import {SpiroDetailsTable} from "../../Widgets/SpiroDetailsTable/SpiroDetailsTable";
import {spiroTestData} from "../../../spr/SpiroTest";
import {SpiroViewer} from "../../Widgets/SpiroViewer/SpiroViewer";
import StarIcon from "../../../assets/svg/StarIcon";
import {logError} from "../../../helpers/LogHelper";


export function SharedSpiroView() {
    const isSupport = useAccessInfo(Role.Support);
    let {recordToken = ""} = useParams();
    const [controller] = useAbortController();
    const token = useAppSelector(selectToken);
    const dispatch = useAppDispatch();
    const [isFetching, setFetchingState] = useState(true);
    const [hasError, setErrorState] = useState(false);
    const [notFound, setNotFoundState] = useState(false);
    const [record, setRecord] = useState(null as Record | null);
    const [data, setData] = useState(null as SpiroData | null);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const dataHandler = (data: SpiroData) => {
        setFetchingState(false);
        setErrorState(false);
        setNotFoundState(false);
        setActiveTabIndex(0);
        setData(data);
    };
    const recordHandler = (record: Record) => {
        setErrorState(false);
        setNotFoundState(false);
        setRecord(record);
    };
    const errorHandler = (error: ErrorResponse) => {
        logError("Spiro data fetch error", error);
        if (!handleErrors(error, dispatch)) {
            if (error.ResultCode === ErrorCodes.NotFound) {
                setNotFoundState(true);
                setErrorState(false);
            } else {
                setErrorState(true);
                setNotFoundState(false);
            }
            setFetchingState(false);
        }
    };
    const fetchData = (record: Record) => {
        if (process.env.NODE_ENV === 'development') {
            //For testing
            dataHandler(spiroTestData);
        } else {
            //Main code
            let userToken = token?.token;
            if (userToken && record.spiroStudy) {
                setFetchingState(true);
                ApiHelper.getSpiroDataWithToken(recordToken, record.spiroStudy.spiroId, controller, dataHandler, errorHandler);
            } else {
                setErrorState(true);
            }
        }
    };
    const fetchRecord = () => {
        let userToken = token?.token;
        if (userToken) {
            setFetchingState(true);
            ApiHelper.getRecordWithToken(recordToken, controller, recordHandler, errorHandler);
        } else {
            setErrorState(true);
        }
    };
    useEffect(() => fetchRecord(), [recordToken]);     // eslint-disable-line
    useEffect(() => {
        if (record) {
            fetchData(record);
        }
    }, [record]);     // eslint-disable-line
    const isOk = !hasError && !notFound && !isFetching;
    const tabButtonClickHandler: (index: number) => void = (index) => {
        if (index !== activeTabIndex) {
            setActiveTabIndex(index);
        }
    };
    const buttons = [
        <div key={`b-0`} className={`tab-round-button ${activeTabIndex === 0 ? "tab-round-button-selected" : ""}`}
             onClick={() => tabButtonClickHandler(0)}>
            <StarIcon/>
        </div>
    ];
    const recordButtons = data?.records.map((r, i) => <div key={`b-${i + 1}`}
                                                           className={`tab-round-button ${activeTabIndex === i + 1 ? "tab-round-button-selected" : ""}`}
                                                           onClick={() => tabButtonClickHandler(i + 1)}>{i + 1}</div>);
    if (recordButtons && recordButtons.length > 1) {
        buttons.push(...recordButtons);
    }
    return (
        <div>
            <Breadcrumbs
                data={new Map([[record?.id ?? "_", record ? formatDateTime(record.dateTime) : null], [record?.patientId ?? "_", record?.patientName ?? null]])}/>
            {isFetching && <AsyncIndicator/>}
            {!isFetching && hasError && <FetchError onRetry={fetchRecord}/>}
            {!isFetching && notFound && <NotFound/>}
            {(isOk && record && data && data.records.length > 0) &&
                <div className="spiro-data-container">
                    {buttons &&
                        <div className="d-flex justify-content-center mt-4 mb-2">
                            {buttons}
                        </div>
                    }
                    <SpiroViewer spiroData={data} activeRecordIndex={activeTabIndex} recordId={record.id}
                                 studyId={record.spiroStudy.spiroId} isSupport={isSupport}/>
                </div>
            }
            {(isOk && record && data) &&
                <SpiroDetailsTable record={record} data={data} isSupport={isSupport}/>
            }
            {(isOk && record) && <Conclusion initialValue={record.stethoscopeStudy.stethoscopeConclusion ?? ""}
                                             cachedValue={record.stethoscopeStudy.stethoscopeConclusion ?? ""}/>}
        </div>
    );
}
